import { useReducer, useEffect } from "react"
import { useHistory, useLocation } from "react-router-dom"
import queryString from "query-string"
import { reducer } from "./reducer"
import {
  setSearchParams,
  setRequestResult,
  setIsFetching,
} from "./actionCreators"
import individualServices from "../../../services/individualServices"
import companyServices from "../../../services/copmanyServices"
import { paths, normalizeAccountTypeFromURL } from "../../../routes/paths"
import freelancerServices from "../../../services/freelancerServices"
import { normalizeMarketForFilter } from "../../../components/CompanyUserInfo/options/marketOptions"

const useSearch = () => {
  const history = useHistory()
  const location = useLocation()

  // Parse initial search params and normalize "professional" to "individual"
  const initialSearchParams = queryString.parse(location.search, {
    arrayFormat: "comma",
  })
  if (initialSearchParams.account_type === "professional") {
    initialSearchParams.account_type = "individual"
  }

  const [state, dispatch] = useReducer(reducer, {
    searchParamGroup: {
      query: "",
      ...initialSearchParams,
    },
    requestResultGroup: {
      query: "",
      account_type: initialSearchParams.account_type || "individual",
      itemList: [],
      pageCount: 0,
      isFetching: true,
    },
  })
  useEffect(() => {
    const locationSearchGroup = queryString.parse(location.search, {
      arrayFormat: "comma",
    })

    // Normalize "professional" from URL back to "individual" for internal logic
    const normalizedSearchGroup = { ...locationSearchGroup }
    if (normalizedSearchGroup.account_type === "professional") {
      normalizedSearchGroup.account_type = "individual"
    }

    // Only update state if params actually changed (to avoid unnecessary re-renders)
    const currentParamsStr = JSON.stringify(state.searchParamGroup)
    const newParamsStr = JSON.stringify(normalizedSearchGroup)
    if (currentParamsStr !== newParamsStr) {
      dispatch(setSearchParams(normalizedSearchGroup))
    }

    // Set loading state before making API call
    dispatch(setIsFetching(true))
    const searchGroupForApi = {
      ...normalizedSearchGroup,
      ...(normalizedSearchGroup.markets && {
        markets: normalizeMarketForFilter(normalizedSearchGroup.markets),
      }),
    }

    // Make API call with normalized account_type
    const accountTypeForAPI = normalizedSearchGroup.account_type || "individual"
    switch (accountTypeForAPI) {
      case "individual": {
        individualServices
          .getIndividuals(searchGroupForApi)
          .then(({ data }) => {
            dispatch(
              setRequestResult({
                itemList: data.individuals || [],
                dataTotalCount: data.total_count || 0,
                locationSearchGroup: normalizedSearchGroup,
              }),
            )
            window.scrollTo(0, 0)
          })
          .catch((error) => {
            console.error("Error fetching individuals:", error)
            dispatch(
              setRequestResult({
                itemList: [],
                dataTotalCount: 0,
                locationSearchGroup: normalizedSearchGroup,
              }),
            )
          })
        break
      }
      case "company": {
        companyServices
          .getCompanies(searchGroupForApi)
          .then(({ data }) => {
            dispatch(
              setRequestResult({
                itemList: data.companies || [],
                dataTotalCount: data.total_count || 0,
                locationSearchGroup: normalizedSearchGroup,
              }),
            )
            window.scrollTo(0, 0)
          })
          .catch((error) => {
            console.error("Error fetching companies:", error)
            dispatch(
              setRequestResult({
                itemList: [],
                dataTotalCount: 0,
                locationSearchGroup: normalizedSearchGroup,
              }),
            )
          })
        break
      }
      case "freelancers": {
        freelancerServices
          .getFreelancers(searchGroupForApi)
          .then(({ data }) => {
            dispatch(
              setRequestResult({
                itemList: data.freelancers,
                dataTotalCount: data.total_count,
                locationSearchGroup: normalizedSearchGroup,
              }),
            )
            window.scrollTo(0, 0)
          })
          .catch((error) => {
            console.error("Error fetching freelancers:", error)
            dispatch(
              setRequestResult({
                itemList: [],
                dataTotalCount: 0,
                locationSearchGroup: normalizedSearchGroup,
              }),
            )
          })
        break
      }
    }
  }, [location.search])

  useEffect(() => {
    // Only update URL if search params changed (excluding account_type normalization)
    // This prevents infinite loops when only account_type is being normalized
    const currentParams = queryString.parse(location.search, {
      arrayFormat: "comma",
    })
    const normalizedCurrentParams = { ...currentParams }
    if (normalizedCurrentParams.account_type === "professional") {
      normalizedCurrentParams.account_type = "individual"
    }

    // Compare normalized params to see if there's a real change
    const paramsChanged =
      JSON.stringify(normalizedCurrentParams) !==
      JSON.stringify(state.searchParamGroup)

    if (paramsChanged) {
      const urlToSet = paths.memberSearch(state.searchParamGroup)
      history.replace(urlToSet)
    }
  }, [state.searchParamGroup, location.search, history])
  return [state, dispatch]
}
export default useSearch
